import { queryLiveDetailServ } from "@/api/live";
import { routesList } from "@/router";
import { formatImgUrl } from "@/utils/crop";
import { platform } from "@/utils/platform";
import { pxtorem } from "@/utils/pxtorem";
import Track from "@/utils/track";
import useNavigation from "@/utils/useNavigate";
import { wxConfig } from "@/utils/wx-config";
import { Image } from "antd-mobile";
import classNames from "classnames";
import React from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import TCPlayer from "tcplayer.js";
import "tcplayer.js/dist/tcplayer.min.css";
import { LivePlayerError, LivePlayerProps, LivePlayerRef } from "../props";
import styles from "./index.module.less";

// 从路由配置获取当前页面的pageName
const getPageNameFromRoute = (pathname: string): string => {
  // 精确匹配
  let match = routesList.find(r => r.path === pathname && r.pageName);
  if (match && match.pageName) {
    return match.pageName;
  }
  // 动态路由（如 /mx-shop-micro/activity/:activityId）
  match = routesList.find(r => {
    if (!r.pageName) {
      return false;
    }
    // 将路由 path 转为正则
    const pattern = r.path.replace(/:[^/]+/g, "[^/]+") + "$";
    return new RegExp(pattern).test(pathname);
  });
  if (match && match.pageName) {
    return match.pageName;
  }
  // 兜底
  return document.title || pathname;
};

const LivePlayer = React.forwardRef<LivePlayerRef, LivePlayerProps>((props, ref) => {
  const track = Track();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const { navigate2LiveItem } = useNavigation();
  const { mutedVisible = true, roomId, trackData, liveInfo, showPlayBtn = false } = props;
  const location = useLocation();

  const idRef = React.useRef(`live-player-id-${props.roomId}`);
  const palyerRef = React.useRef<TCPlayer>();
  const videoRef = React.useRef<HTMLVideoElement>(null);

  // 透传 ref
  React.useImperativeHandle(ref, () => ({
    play: () => palyerRef.current?.play(),
    pause: () => palyerRef.current?.pause(),
  }));

  const [playBtnVisible, setPlayBtnVisible] = React.useState(false);
  const [muted, setMuted] = React.useState(props.muted);
  const [liveData, setLiveData] = React.useState<any>();

  const handleTrack = (eventName: string) => {
    let obj = {
      ...(trackData || {}),
      page_name: trackData?.page_name || getPageNameFromRoute(location.pathname),
      live_status:
        liveData?.liveStatus === 20
          ? "直播中"
          : liveData?.liveStatus === 10
            ? "直播预告"
            : "直播结束",
      live_no: liveData?.liveNo || "",
      live_name: liveData?.liveTitle || "",
      live_type: liveData?.liveType || "",
      activity_id: params?.activityId || searchParams.get("activityId") || "",
      merchant_shop_code: searchParams.get("merchantShopCode") || "",
      activity_spu_code: trackData?.activity_spu_code || searchParams.get("activitySpuId") || "",
    };
    if (eventName === "live_entrance_click") {
      obj.btn_name = "进入直播";
    }
    track.track(eventName, obj);
  };

  // 查直播信息
  React.useEffect(() => {
    console.log("player-props", props);
    if (props?.src) {
      setLiveData({
        liveNo: props.roomId,
        pullStreamUrl: {
          httpM3u8Url: props.src,
        },
        ...(liveInfo || {}),
      });
      return;
    } else {
      queryLiveDetailServ(roomId).then((res: any) => {
        setLiveData(res);
        console.log("liveData", res);
      });
    }
  }, []);

  React.useEffect(() => {
    if (liveData?.pullStreamUrl?.httpM3u8Url && videoRef.current) {
      handleTrack("live_entrance_expose");
      const play = () => {
        console.log("play()执行");
        if (platform.isiOSH5) {
          wxConfig({
            onReady: () => {
              palyerRef.current?.play();
            },
          });
        } else {
          palyerRef.current?.play();
        }
      };
      palyerRef.current = TCPlayer(videoRef.current, {
        sources: [
          {
            src: liveData?.pullStreamUrl?.httpM3u8Url,
          },
        ],
        preload: "auto",
        autoplay: true,
        muted: props.muted,
        licenseUrl: "https://license.vod2.myqcloud.com/license/v2/1300172750_1/v_cube.license",
        poster: props.poster,
        controls: false,
      });
      palyerRef.current?.on("error", (error: LivePlayerError) => {
        console.error("onError", error);
        setPlayBtnVisible(false);
        props.onError?.(error);
        palyerRef.current?.dispose();
        // setTimeout(play, 3000);
      });
      // 自动播放被浏览器阻止时触发。（原 2005 回调事件统一合并到 blocked 事件中）。
      palyerRef.current?.on("blocked", (info: any) => {
        console.error("onBlocked", info);
        setPlayBtnVisible(true);
      });
      palyerRef.current?.on("canplay", (info: any) => {
        console.error("onCanplay", info);
        play();
      });

      setTimeout(play, 20);
      return () => palyerRef.current?.dispose();
    }
  }, [liveData]);

  React.useEffect(() => {
    setMuted(props.muted);
  }, [props.muted]);

  const volumeRef = React.useRef(1);
  React.useEffect(() => {
    try {
      palyerRef.current?.setMuted(muted);
    } catch (error) {
      if (muted) {
        volumeRef.current = palyerRef.current?.volume();
        palyerRef.current?.volume(0);
      } else {
        palyerRef.current?.volume(volumeRef.current);
      }
    }
  }, [muted]);

  return (
    <div
      className={classNames(styles.container, props.className)}
      style={{
        ...props.style,
        ...(props.width
          ? { width: typeof props.width === "string" ? props.width : pxtorem(props.width) }
          : undefined),
        ...(props.height
          ? { height: typeof props.height === "string" ? props.height : pxtorem(props.height) }
          : undefined),
      }}
      onClick={() => {
        console.log("liveData", liveData, props?.supportClick, liveData?.liveNo);
        if (props?.supportClick && liveData?.liveNo) {
          handleTrack("live_entrance_click");
          navigate2LiveItem(liveData);
        }
      }}>
      {/* eslint-disable-next-line no-restricted-syntax */}
      <video
        ref={videoRef}
        id={idRef.current}
        className={styles.video}
        style={{
          objectFit: "cover",
          pointerEvents: "none", // 关键
        }}
        width={props.width}
        height={props.height}
        poster={props.poster}
        muted={muted}
        autoPlay={true}
        preload="auto"
        playsInline={true}
        webkit-playsinline="true"
        x5-playsinline="true"
        x5-video-player-type="h5-page"
        x5-video-player-fullscreen="true"
        x5-video-orientation="portrait"
      />
      <Image
        src={formatImgUrl(
          props.poster,
          props.width ? Number(props.width) : undefined,
          props.height ? Number(props.height) : undefined,
        )}
        className={styles.poster}
        fit="cover"
        style={{
          pointerEvents: "none", // 关键
        }}
      />
      {mutedVisible ? (
        <img
          src={
            muted
              ? "https://akim-oss.aikucun.com/e4df8c87f54bddf0a4264d4fd221aea37d08c471_1750298344138_69.png"
              : "https://akim-oss.aikucun.com/32f167b6ab4c860c144ff7fc9df18b9695f53fe1_1750298338569_19.png"
          }
          className={styles.muted}
          onClick={e => {
            e.stopPropagation?.();
            setMuted(!muted);
          }}
        />
      ) : null}
      {showPlayBtn && playBtnVisible ? (
        <img
          src="https://akim-oss.aikucun.com/mshop/a58bd809b67a675e7aa273022018c81f7e381c1c_1756967800193_39.png"
          className={styles.playBtn}
          onClick={() => {
            palyerRef.current?.play();
            setPlayBtnVisible(false);
          }}
        />
      ) : null}
    </div>
  );
});

export default LivePlayer;
